import DOMPurify from 'dompurify'

// Formatting only: no images, media, forms, styles or style attributes, which could load
// something from another site, restyle the app or show a fake sign-in form.
const RICH_TEXT_TAGS = [
  'p',
  'br',
  'div',
  'span',
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'ul',
  'ol',
  'li',
  'a',
  'blockquote',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'pre',
  'code',
  'table',
  'thead',
  'tbody',
  'tr',
  'td',
  'th',
  'hr',
]

// What a mention's id can be: a user id (a UUID) or Coach.
const MENTION_ID = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|coachLogic)$/i

/**
 * Only a mention needs its id, and only one that names a person or Coach. Any other id could stand
 * in for one of the page's own elements (e.g. an upload field's input, found by its id).
 */
export const keepMentionIdsOnly = (
  node: Element,
  data: { attrName: string; attrValue: string; keepAttr: boolean }
) => {
  if (data.attrName !== 'id') return
  if (node.nodeName.toLowerCase() !== 'mention' || !MENTION_ID.test(data.attrValue)) {
    data.keepAttr = false
  }
}

/**
 * Rich text written by other people, synced calendars or Coach (task and meeting descriptions):
 * show the formatting, never run or load anything from it. Links open in a new tab.
 * `mentions` keeps the editor's `<mention id="…">` tags.
 */
export const sanitizeRichTextHtml = (
  html: string,
  { mentions = false }: { mentions?: boolean } = {}
): string => {
  if (!html) return ''
  // No data-* or aria-* attributes either: react-tooltip renders data-tooltip-html as HTML.
  if (mentions) DOMPurify.addHook('uponSanitizeAttribute', keepMentionIdsOnly as any)
  let clean: string
  try {
    clean = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: mentions ? [...RICH_TEXT_TAGS, 'mention'] : RICH_TEXT_TAGS,
      ALLOWED_ATTR: mentions ? ['href', 'id'] : ['href'],
      ALLOW_DATA_ATTR: false,
      ALLOW_ARIA_ATTR: false,
    })
  } finally {
    if (mentions) DOMPurify.removeHook('uponSanitizeAttribute')
  }
  const container = document.createElement('div')
  container.innerHTML = clean
  container.querySelectorAll('a').forEach((link) => {
    link.setAttribute('target', '_blank')
    link.setAttribute('rel', 'noopener noreferrer')
  })
  return container.innerHTML
}
